import { useRef, useState } from "react";
import { ExternalLink, FileUp, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { provider_id: "", document_type: "Background Check", issue_date: "", expiration_date: "", result: "", notes: "" };

export const ComplianceDocumentForm = ({ initial, providers, catalog, customTypes = [], onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState("");
  const fileInput = useRef(null);
  const set = (patch) => setForm({ ...form, ...patch });
  const isHealth = catalog.health_screening_types.includes(form.document_type);
  async function submit(event) {
    event.preventDefault();
    try {
      const body = new FormData();
      ["provider_id", "document_type", "issue_date", "expiration_date", "result", "notes"].forEach((key) => body.append(key, form[key] || ""));
      const file = fileInput.current?.files?.[0];
      if (file) body.append("evidence", file);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = initial?.id ? await api.put(`/compliance/documents/${initial.id}`, body, config) : await api.post("/compliance/documents", body, config);
      onSaved(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }
  return (
    <form className="inline-edit-form compliance-doc-form" data-testid="compliance-document-form" onSubmit={submit}>
      <label>Provider
        <select required value={form.provider_id} data-testid="compliance-doc-provider-select" disabled={!!initial?.id} onChange={(e) => set({ provider_id: e.target.value })}>
          <option value="">Select provider</option>
          {providers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.discipline}</option>)}
        </select>
      </label>
      <label>Document type
        <select value={form.document_type} data-testid="compliance-doc-type-select" disabled={!!initial?.id} onChange={(e) => set({ document_type: e.target.value, result: "" })}>
          {catalog.document_types.map((t) => <option key={t}>{t}</option>)}
          {customTypes.map((t) => <option key={t}>{t} (custom)</option>)}
        </select>
      </label>
      <label>Issue date<input required type="date" value={form.issue_date} data-testid="compliance-doc-issue-input" onChange={(e) => set({ issue_date: e.target.value })} /></label>
      <label>Expiration date<input required type="date" value={form.expiration_date} data-testid="compliance-doc-expiration-input" onChange={(e) => set({ expiration_date: e.target.value })} /></label>
      {isHealth && (
        <label>Result (restricted)
          <select value={form.result} data-testid="compliance-doc-result-select" onChange={(e) => set({ result: e.target.value })}>
            <option value="">Not recorded</option><option>Negative</option><option>Positive</option><option>Pending</option>
          </select>
        </label>
      )}
      <label>Notes<input value={form.notes} data-testid="compliance-doc-notes-input" onChange={(e) => set({ notes: e.target.value })} /></label>
      <label className="compliance-doc-file"><FileUp size={14} /> Evidence (PDF, DOCX or image, 15 MB max)
        <input ref={fileInput} type="file" accept="application/pdf,.docx,image/png,image/jpeg,image/webp,image/heic" data-testid="compliance-doc-file-input" />
      </label>
      {error && <p className="form-message error-message" data-testid="compliance-doc-form-error">{error}</p>}
      <div className="button-row">
        <button className="button button-primary" data-testid="save-compliance-doc-button"><Plus size={14} /> {initial?.id ? "Save document" : "Add compliance document"}</button>
        <button type="button" className="button button-secondary" data-testid="cancel-compliance-doc-button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

export const ComplianceDocumentRow = ({ record, onEdit, onDeleted }) => {
  const [error, setError] = useState("");
  async function open() {
    try {
      const response = await api.get(`/compliance/documents/${record.id}/evidence-link`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${response.data.path}`, "_blank", "noopener");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }
  async function remove() {
    try {
      await api.delete(`/compliance/documents/${record.id}`);
      onDeleted(record);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }
  const state = !record.valid ? "Expired" : record.result === "Pending" ? "Pending" : "Cleared";
  return (
    <tr className="credential-group-child compliance-doc-row" data-testid={`compliance-doc-${record.id}`}>
      <td><b>{record.provider_name}</b><span>{record.provider_discipline}</span></td>
      <td>{record.document_type}{record.restricted && <Lock size={12} className="compliance-restricted-icon" aria-label="Stored apart; System Administrator and HR only" />}<span>Issued {record.issue_date}{record.result ? ` · result ${record.result}` : ""}{record.notes ? ` · ${record.notes}` : ""}</span></td>
      <td>{record.expiration_date}<span>{record.days_remaining} days remaining{record.alert_level ? ` · re-check alert (${record.alert_level}-day)` : ""}</span></td>
      <td>{record.evidence ? <button type="button" className="table-edit-button compliance-text-button" data-testid={`open-compliance-evidence-${record.id}`} onClick={open}><ExternalLink size={13} /> {record.evidence.original_filename}</button> : <span className="pairing-meta">No evidence</span>}</td>
      <td><em className={`compliance-result compliance-result-${state.toLowerCase()}`} data-testid={`compliance-doc-state-${record.id}`}>{state}</em></td>
      <td>{record.restricted ? "Restricted" : "Compliance"}</td>
      <td>—</td>
      <td className="credential-actions">
        <button type="button" className="table-edit-button" data-testid={`edit-compliance-doc-${record.id}`} onClick={() => onEdit(record)}><Pencil size={14} /> Edit</button>
        <button type="button" className="table-edit-button table-delete-button" data-testid={`delete-compliance-doc-${record.id}`} onClick={remove}><Trash2 size={14} /> Delete</button>
        {error && <small className="form-message error-message">{error}</small>}
      </td>
    </tr>
  );
};
